/**
 * TransactionFilters - Advanced Filtering Component
 * 
 * Rich filtering experience with visual feedback and quick filters
 * OPTIMIZED with React.memo
 */

import { useState, memo } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Calendar } from '../../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Separator } from '../../../components/ui/separator';
import { 
  Search, 
  Filter, 
  X, 
  CalendarIcon, 
  TrendingUp, 
  TrendingDown,
  CheckCircle2,
  Clock,
  Tag,
  DollarSign,
  SlidersHorizontal,
  Calendar as CalendarDays,
  CalendarRange,
  CalendarClock,
  Infinity,
  Landmark,
} from 'lucide-react';
import { cn } from '../../../components/ui/utils';
import { formatDate } from '../../../lib/utils/format';

export interface TransactionFilterValues {
  search: string;
  entryType: 'all' | 'income' | 'expense' | 'transfer';
  paymentStatus: 'all' | 'confirmado' | 'previsão';
  viewMode?: 'all' | 'monthly' | 'ytd' | 'yearly' | 'all-time';
  dateFrom?: Date;
  dateTo?: Date;
  category?: string;
  minAmount?: number;
  maxAmount?: number;
  isRecurring?: boolean;
  bankAccountId?: string;
}

export interface BankAccountOption {
  id: string;
  account_name: string;
}

interface TransactionFiltersProps {
  filters: TransactionFilterValues;
  onFiltersChange: (filters: TransactionFilterValues) => void;
  categories: string[];
  bankAccounts?: BankAccountOption[];
}

function TransactionFiltersComponent({
  filters,
  onFiltersChange,
  categories,
  bankAccounts = [],
}: TransactionFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const activeFilterCount = [
    filters.entryType !== 'all',
    filters.paymentStatus !== 'all',
    filters.viewMode && filters.viewMode !== 'all',
    !!filters.dateFrom,
    !!filters.dateTo,
    !!filters.category,
    !!filters.minAmount,
    !!filters.maxAmount,
    filters.isRecurring !== undefined,
    !!filters.bankAccountId,
  ].filter(Boolean).length;

  const quickFilters = [
    {
      label: 'Receitas',
      icon: TrendingUp,
      active: filters.entryType === 'income',
      onClick: () => onFiltersChange({
        ...filters,
        entryType: filters.entryType === 'income' ? 'all' : 'income',
      }),
      color: 'text-success-600 bg-success-50 hover:bg-success-100 border-success-200',
    },
    {
      label: 'Despesas',
      icon: TrendingDown,
      active: filters.entryType === 'expense',
      onClick: () => onFiltersChange({
        ...filters,
        entryType: filters.entryType === 'expense' ? 'all' : 'expense',
      }),
      color: 'text-danger-600 bg-danger-50 hover:bg-danger-100 border-danger-200',
    },
    {
      label: 'Confirmado',
      icon: CheckCircle2,
      active: filters.paymentStatus === 'confirmado',
      onClick: () => onFiltersChange({
        ...filters,
        paymentStatus: filters.paymentStatus === 'confirmado' ? 'all' : 'confirmado',
      }),
      color: 'text-info-600 bg-info-50 hover:bg-info-100 border-info-200',
    },
    {
      label: 'Previsão',
      icon: Clock,
      active: filters.paymentStatus === 'previsão',
      onClick: () => onFiltersChange({
        ...filters,
        paymentStatus: filters.paymentStatus === 'previsão' ? 'all' : 'previsão',
      }),
      color: 'text-orange-600 bg-orange-50 hover:bg-orange-100 border-orange-200',
    },
    {
      label: 'Mensal',
      icon: CalendarDays,
      active: filters.viewMode === 'monthly',
      onClick: () => onFiltersChange({
        ...filters,
        viewMode: filters.viewMode === 'monthly' ? 'all' : 'monthly',
      }),
      color: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
    },
    {
      label: 'Até Hoje',
      icon: CalendarClock,
      active: filters.viewMode === 'ytd',
      onClick: () => onFiltersChange({
        ...filters,
        viewMode: filters.viewMode === 'ytd' ? 'all' : 'ytd',
      }),
      color: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
    },
    {
      label: 'Anual',
      icon: CalendarRange,
      active: filters.viewMode === 'yearly',
      onClick: () => onFiltersChange({
        ...filters,
        viewMode: filters.viewMode === 'yearly' ? 'all' : 'yearly',
      }),
      color: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
    },
    {
      label: 'Tudo',
      icon: Infinity,
      active: filters.viewMode === 'all-time',
      onClick: () => onFiltersChange({
        ...filters,
        viewMode: filters.viewMode === 'all-time' ? 'all' : 'all-time',
      }),
      color: 'text-purple-600 bg-purple-50 hover:bg-purple-100 border-purple-200',
    },
  ];

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      entryType: 'all',
      paymentStatus: 'all',
      viewMode: 'all',
      bankAccountId: undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por descrição, categoria, valor..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="pl-10 pr-10 h-12 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label="Campo de busca de lançamentos"
          role="searchbox"
        />
        {filters.search && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 focus-visible:ring-2 focus-visible:ring-primary"
            onClick={() => onFiltersChange({ ...filters, search: '' })}
            aria-label="Limpar busca"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Limpar busca</span>
          </Button>
        )}
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2">
        {quickFilters.map((filter) => {
          const Icon = filter.icon;
          return (
            <Button
              key={filter.label}
              variant="outline"
              size="sm"
              onClick={filter.onClick}
              className={cn(
                'h-9 transition-all focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                filter.active && filter.color
              )}
              aria-label={`Filtrar por ${filter.label}`}
              aria-pressed={filter.active}
            >
              <Icon className="h-4 w-4 mr-2" />
              {filter.label}
            </Button>
          );
        })}

        <Separator orientation="vertical" className="h-9 mx-1" />

        {/* Advanced Filters Toggle */}
        <Popover open={showAdvanced} onOpenChange={setShowAdvanced}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-9 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={`Filtros avançados${activeFilterCount > 0 ? ` (${activeFilterCount} ativos)` : ''}`}
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Avançado
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 min-w-[20px]">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[400px] p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Filtros Avançados</h4>
                {activeFilterCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label="Limpar todos os filtros"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Limpar
                  </Button>
                )}
              </div>

              <Separator />

              {/* Date Range */}
              <div className="space-y-2">
                <Label className="text-sm">Período</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="justify-start text-left font-normal focus-visible:ring-2 focus-visible:ring-primary"
                        aria-label="Selecionar data inicial"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateFrom ? formatDate(filters.dateFrom.toISOString(), 'short') : 'De'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom}
                        onSelect={(date) => onFiltersChange({ ...filters, dateFrom: date })}
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="justify-start text-left font-normal focus-visible:ring-2 focus-visible:ring-primary"
                        aria-label="Selecionar data final"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateTo ? formatDate(filters.dateTo.toISOString(), 'short') : 'Até'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo}
                        onSelect={(date) => onFiltersChange({ ...filters, dateTo: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label className="text-sm">Categoria</Label>
                <Select
                  value={filters.category || 'all'}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    category: value === 'all' ? undefined : value 
                  })}
                >
                  <SelectTrigger aria-label="Selecionar categoria">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bank Account */}
              <div className="space-y-2">
                <Label className="text-sm">Conta Bancária</Label>
                <Select
                  value={filters.bankAccountId || 'all'}
                  onValueChange={(value) => onFiltersChange({ 
                    ...filters, 
                    bankAccountId: value === 'all' ? undefined : value 
                  })}
                >
                  <SelectTrigger aria-label="Selecionar conta bancária">
                    <Landmark className="h-4 w-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Todas as contas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as contas</SelectItem>
                    {bankAccounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount Range */}
              <div className="space-y-2">
                <Label className="text-sm">Faixa de Valor</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filters.minAmount || ''}
                      onChange={(e) => onFiltersChange({
                        ...filters,
                        minAmount: e.target.value ? Number(e.target.value) : undefined,
                      })}
                      className="pl-10 focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label="Valor mínimo"
                    />
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filters.maxAmount || ''}
                      onChange={(e) => onFiltersChange({
                        ...filters,
                        maxAmount: e.target.value ? Number(e.target.value) : undefined,
                      })}
                      className="pl-10 focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label="Valor máximo"
                    />
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All (when filters active) */}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-9 text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Limpar todos os filtros ativos"
          >
            <X className="h-4 w-4 mr-2" />
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">Filtros ativos:</span>
          
          {filters.entryType !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Tipo: {filters.entryType === 'income' ? 'Receita' : filters.entryType === 'expense' ? 'Despesa' : 'Transferência'}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, entryType: 'all' })}
              />
            </Badge>
          )}

          {filters.paymentStatus !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Status: {filters.paymentStatus === 'confirmado' ? 'Confirmado' : 'Previsão'}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, paymentStatus: 'all' })}
              />
            </Badge>
          )}

          {filters.category && (
            <Badge variant="secondary" className="gap-1">
              <Tag className="h-3 w-3" />
              {filters.category}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, category: undefined })}
              />
            </Badge>
          )}

          {(filters.dateFrom || filters.dateTo) && (
            <Badge variant="secondary" className="gap-1">
              <CalendarIcon className="h-3 w-3" />
              {filters.dateFrom && formatDate(filters.dateFrom.toISOString(), 'short')}
              {filters.dateFrom && filters.dateTo && ' - '}
              {filters.dateTo && formatDate(filters.dateTo.toISOString(), 'short')}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, dateFrom: undefined, dateTo: undefined })}
              />
            </Badge>
          )}

          {(filters.minAmount || filters.maxAmount) && (
            <Badge variant="secondary" className="gap-1">
              <DollarSign className="h-3 w-3" />
              {filters.minAmount && `Min: ${filters.minAmount}`}
              {filters.minAmount && filters.maxAmount && ' - '}
              {filters.maxAmount && `Max: ${filters.maxAmount}`}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, minAmount: undefined, maxAmount: undefined })}
              />
            </Badge>
          )}

          {filters.bankAccountId && (
            <Badge variant="secondary" className="gap-1">
              <Landmark className="h-3 w-3" />
              {bankAccounts.find(a => a.id === filters.bankAccountId)?.account_name || 'Conta'}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFiltersChange({ ...filters, bankAccountId: undefined })}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export const TransactionFilters = memo(TransactionFiltersComponent);
